import { EVENT_BUS } from '@/lib/event-bus';

import { discountRuleLookupApi } from '../discount-rule-lookup/discount-rule-lookup.api';
import { integrationsLookupApi } from '../integrations-lookup/integrations-lookup.api';
import { notificationTemplateLookupApi } from '../notification-template-lookup/notification-template-lookup.api';
import { paymentsDeskLookupApi } from '../payments-desk-lookup/payments-desk-lookup.api';
import { roleLookupApi } from '../role-lookup/role-lookup.api';
import { subscriptionProductLookupApi } from '../subscription-product-lookup/subscription-product-lookup.api';
import { userLookupApi } from '../user-lookup/user-lookup.api';

/** Just enough of the store to dispatch — the factory calls this, not a component. */
type Dispatch = (action: unknown) => unknown;

/**
 * Keeps the W6 lookup bridges honest.
 *
 * The bridges hold their OWN cache of roles and users, separate from the module
 * that owns them. Rename a role in the editor and the editor's list refreshes
 * through RTK tag invalidation — but every `ServerSelect` backed by
 * `role-lookup` keeps showing the old name, because the two caches have no
 * relationship. That is precisely the "never make the admin reload" failure W5
 * exists to prevent, and the bus is how the two sides talk without importing
 * each other.
 *
 * Wired at store construction rather than from a component, so the invalidation
 * happens whether or not anything is currently rendering a lookup — a stale
 * entry must not survive until the next time a picker is opened.
 */
export function subscribeLookupInvalidation(dispatch: Dispatch): () => void {
  const unsubscribes = [
    EVENT_BUS.subscribeTo('access.role.changed', () => {
      dispatch(roleLookupApi.util.invalidateTags(['RoleLookup']));
    }),
    EVENT_BUS.subscribeTo('members.status.changed', () => {
      // A suspended member's status is rendered as the option's description, so
      // a lookup that missed the change would describe them as approved.
      dispatch(userLookupApi.util.invalidateTags(['UserLookup']));
    }),
    EVENT_BUS.subscribeTo('access.staff.changed', () => {
      dispatch(userLookupApi.util.invalidateTags(['UserLookup']));
    }),
    EVENT_BUS.subscribeTo('billing.product.changed', () => {
      // A renamed or withdrawn plan is named on the order desk's picker, on the
      // orders queue's plan column and in a discount rule's scope — none of
      // which lives in the catalogue's own cache. The event carries an id and
      // this deliberately ignores it: a delete answers 204 and names nothing,
      // and the lookup still has to forget the plan.
      dispatch(subscriptionProductLookupApi.util.invalidateTags(['ProductLookup']));
    }),
    EVENT_BUS.subscribeTo('billing.order.changed', () => {
      // A decision on the desk moves the count the sidebar wears (F3); waiting
      // for the next poll would leave a badge saying "1" over an empty queue.
      dispatch(paymentsDeskLookupApi.util.invalidateTags(['PaymentsDesk']));
    }),
    EVENT_BUS.subscribeTo('courses.rule.changed', () => {
      // The payload names what the rule PAYS FOR — a course or a test — and
      // never the rule itself, so there is nothing here a lookup keyed by rule
      // id could narrow on. It invalidates the whole tag, which is also what a
      // delete needs: that answers 204 and carries `null` for both fields.
      dispatch(discountRuleLookupApi.util.invalidateTags(['DiscountRuleLookup']));
    }),
    EVENT_BUS.subscribeTo('notifications.template.changed', () => {
      // `authoredChannels` just moved: the rule form's picker must stop saying
      // "nobody has written this yet" about copy that now exists, and the
      // template screen's own unauthored list must lose the row it just gained
      // copy for.
      dispatch(notificationTemplateLookupApi.util.invalidateTags(['NotificationCatalogue']));
    }),
    EVENT_BUS.subscribeTo('integrations.config.changed', () => {
      // A provider activated or re-credentialed from the Integrations screen
      // must clear the Templates page's "email has no working provider" warning
      // without a reload. Probes publish nothing; that lookup refetches on mount.
      dispatch(integrationsLookupApi.util.invalidateTags(['IntegrationsOverviewLookup']));
    }),
  ];

  return () => {
    for (const unsubscribe of unsubscribes) {
      unsubscribe();
    }
  };
}
